import React from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Divider,
  Alert,
  Paper,
} from '@mui/material';
import {
  Store as StoreIcon,
  Search as SearchIcon,
  Favorite as FavoriteIcon,
  History as HistoryIcon,
  Map as MapIcon,
} from '@mui/icons-material';
import StoreSelectImproved from './StoreSelectImproved';

const StoreSelectDemoFixed: React.FC = () => {
  const [selectedStore1, setSelectedStore1] = React.useState('');
  const [selectedStore2, setSelectedStore2] = React.useState('');
  const [selectedStore3, setSelectedStore3] = React.useState('');

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography variant="h3" component="h1" gutterBottom sx={{ mb: 4 }}>
        改良された店舗選択コンポーネント
      </Typography>

      <Alert severity="info" sx={{ mb: 4 }}>
        店舗選択がより直感的になりました！検索、お気に入り、最近使用した店舗などの機能が追加されています。
      </Alert>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        {/* 機能紹介と使い方 */}
        <Box sx={{ display: 'flex', gap: 4, flexDirection: { xs: 'column', md: 'row' } }}>
          <Box sx={{ flex: 1 }}>
            <Card>
              <CardContent>
                <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <StoreIcon />
                  新機能
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <SearchIcon color="primary" />
                    <Typography>リアルタイム検索機能</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <FavoriteIcon color="secondary" />
                    <Typography>お気に入り店舗の管理</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <HistoryIcon color="primary" />
                    <Typography>最近使用した店舗の履歴</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <MapIcon color="action" />
                    <Typography>地図表示（今後実装予定）</Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Box>

          <Box sx={{ flex: 1 }}>
            <Card>
              <CardContent>
                <Typography variant="h5" gutterBottom>
                  使い方
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Typography variant="body2">
                    1. 検索バーをクリックまたは文字を入力
                  </Typography>
                  <Typography variant="body2">
                    2. 店舗名や住所で絞り込み
                  </Typography>
                  <Typography variant="body2">
                    3. カード形式で店舗を視覚的に選択
                  </Typography>
                  <Typography variant="body2">
                    4. ♡ボタンでお気に入りに追加/削除
                  </Typography>
                  <Typography variant="body2">
                    5. 選択した店舗は自動的に履歴に保存
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Box>
        </Box>

        {/* デモ1: 基本的な使用例 */}
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            デモ1: 基本的な店舗選択
          </Typography>
          <StoreSelectImproved
            value={selectedStore1}
            onChange={(storeId) => setSelectedStore1(storeId)}
            label="店舗を選択"
            placeholder="店舗名を検索してください"
          />
          {selectedStore1 && (
            <Alert severity="success" sx={{ mt: 2 }}>
              選択された店舗ID: {selectedStore1}
            </Alert>
          )}
        </Paper>

        <Divider />

        {/* デモ2: 管理者向け（手動入力付き） */}
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            デモ2: 管理者向け（手動入力機能付き）
          </Typography>
          <StoreSelectImproved
            value={selectedStore2}
            onChange={(storeId) => setSelectedStore2(storeId)}
            label="店舗を選択（管理者向け）"
            placeholder="店舗を検索または直接入力"
            allowManualInput={true}
            showMap={true}
            helperText="管理者は店舗IDを直接入力することも可能です"
          />
          {selectedStore2 && (
            <Alert severity="success" sx={{ mt: 2 }}>
              選択された店舗ID: {selectedStore2}
            </Alert>
          )}
        </Paper>

        <Divider />

        {/* デモ3: エラー状態 */}
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            デモ3: エラー状態の表示
          </Typography>
          <StoreSelectImproved
            value={selectedStore3}
            onChange={(storeId) => setSelectedStore3(storeId)}
            label="店舗を選択"
            placeholder="必須項目です"
            required={true}
            error={!selectedStore3}
            helperText={!selectedStore3 ? "店舗を選択してください" : "選択完了"}
            maxHeight={300}
          />
        </Paper>

        {/* 技術仕様 */}
        <Card>
          <CardContent>
            <Typography variant="h5" gutterBottom>
              技術仕様
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Typography variant="body2">
                <strong>状態管理:</strong> LocalStorageを使用してお気に入りと履歴を永続化
              </Typography>
              <Typography variant="body2">
                <strong>パフォーマンス:</strong> useMemoを使用した効率的なフィルタリング
              </Typography>
              <Typography variant="body2">
                <strong>アクセシビリティ:</strong> ARIAラベル、キーボードナビゲーション対応
              </Typography>
              <Typography variant="body2">
                <strong>レスポンシブ:</strong> モバイル・デスクトップ両対応
              </Typography>
              <Typography variant="body2">
                <strong>型安全性:</strong> TypeScriptによる完全な型定義
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Container>
  );
};

export default StoreSelectDemoFixed;